import React, { useMemo, useState } from 'react';
import type { AssetCategory, AssetCondition, AssetCurrency, AssetKind, AssetLocation, AssetStatus, RestaurantAsset } from '../../types';
import {
  ASSET_CATEGORIES,
  ASSET_CONDITIONS,
  ASSET_CURRENCIES,
  ASSET_KINDS,
  ASSET_LOCATIONS,
  ASSET_STATUSES,
  emptyAssetBulkRow,
  restaurantAssetFromBulkRow,
  type AssetBulkRow,
} from '../../lib/assets';
import PlusIcon from '../icons/PlusIcon';
import TrashIcon from '../icons/TrashIcon';

interface AssetBulkAddModalProps {
  onClose: () => void;
  onSave: (assets: RestaurantAsset[]) => Promise<void> | void;
}

const cell =
  'w-full min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-pink-500 focus:border-pink-500';

function startingRows(): AssetBulkRow[] {
  return Array.from({ length: 5 }, () => emptyAssetBulkRow());
}

const AssetBulkAddModal: React.FC<AssetBulkAddModalProps> = ({ onClose, onSave }) => {
  const [rows, setRows] = useState<AssetBulkRow[]>(startingRows);
  const [saving, setSaving] = useState(false);

  const namedCount = useMemo(
    () => rows.filter((r) => r.name.trim()).length,
    [rows],
  );

  const updateRow = (key: string, patch: Partial<AssetBulkRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows((prev) => {
      const lastNamed = [...prev].reverse().find((r) => r.name.trim());
      const last = lastNamed ?? prev[prev.length - 1];
      return [
        ...prev,
        emptyAssetBulkRow({
          kind: last?.kind,
          category: last?.category,
          location: last?.location,
          quantity: last?.kind === 'tagged' ? '1' : last?.quantity ?? '1',
          unitCostCurrency: last?.unitCostCurrency,
          condition: last?.condition,
          status: last?.status,
        }),
      ];
    });
  };

  const removeRow = (key: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const row = rows[rowIndex];
    if (row?.name.trim() && rowIndex === rows.length - 1) {
      addRow();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const assets = rows
      .map(restaurantAssetFromBulkRow)
      .filter((a): a is RestaurantAsset => a != null);
    if (assets.length === 0) {
      alert('Enter a name on at least one row.');
      return;
    }
    setSaving(true);
    try {
      await onSave(assets);
    } catch (err) {
      console.error('Failed to bulk add assets:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 sm:p-5"
      data-soft-keyboard-handled
    >
      <div
        className="flex w-full max-w-[96rem] max-h-[92vh] flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Bulk add assets</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              One row per item. Empty name rows are skipped. Enter on the last named row adds another.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-auto px-5 py-4">
            <table className="min-w-[1100px] w-full border-collapse">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="sticky left-0 z-10 bg-white py-2 pr-2 w-10">#</th>
                  <th className="py-2 pr-2 min-w-[160px]">Name</th>
                  <th className="py-2 pr-2 w-32">Type</th>
                  <th className="py-2 pr-2 w-40">Category</th>
                  <th className="py-2 pr-2 w-28">Location</th>
                  <th className="py-2 pr-2 w-20">Qty</th>
                  <th className="py-2 pr-2 w-28">Replace / tag</th>
                  <th className="py-2 pr-2 w-24">Condition</th>
                  <th className="py-2 pr-2 w-24">Currency</th>
                  <th className="py-2 pr-2 w-24">Amount</th>
                  <th className="py-2 pr-2 w-28">Status</th>
                  <th className="py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.key} className="align-top border-t border-gray-100">
                    <td className="sticky left-0 z-10 bg-white py-1.5 pr-2 text-xs text-gray-400">
                      {index + 1}
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => updateRow(row.key, { name: e.target.value })}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        data-soft-keyboard-handled
                        className={cell}
                        placeholder="e.g. Dinner plates"
                        aria-label={`Name row ${index + 1}`}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={row.kind}
                        onChange={(e) => {
                          const kind = e.target.value as AssetKind;
                          updateRow(row.key, {
                            kind,
                            quantity: kind === 'tagged' ? '1' : row.quantity,
                          });
                        }}
                        className={cell}
                        aria-label={`Type row ${index + 1}`}
                      >
                        {ASSET_KINDS.map((k) => (
                          <option key={k.id} value={k.id}>{k.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={row.category}
                        onChange={(e) => updateRow(row.key, { category: e.target.value as AssetCategory })}
                        className={cell}
                        aria-label={`Category row ${index + 1}`}
                      >
                        {ASSET_CATEGORIES.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={row.location}
                        onChange={(e) => updateRow(row.key, { location: e.target.value as AssetLocation })}
                        className={cell}
                        aria-label={`Location row ${index + 1}`}
                      >
                        {ASSET_LOCATIONS.map((l) => (
                          <option key={l.id} value={l.id}>{l.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={row.kind === 'tagged' ? '1' : row.quantity}
                        disabled={row.kind === 'tagged'}
                        onChange={(e) => updateRow(row.key, { quantity: e.target.value })}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        data-soft-keyboard-handled
                        className={cell}
                        aria-label={`Quantity row ${index + 1}`}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      {row.kind === 'quantity' ? (
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={row.replaceBelow}
                          onChange={(e) => updateRow(row.key, { replaceBelow: e.target.value })}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          data-soft-keyboard-handled
                          className={cell}
                          placeholder="optional"
                          aria-label={`Replace below row ${index + 1}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={row.assetTag}
                          onChange={(e) => updateRow(row.key, { assetTag: e.target.value })}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          data-soft-keyboard-handled
                          className={cell}
                          placeholder="tag"
                          aria-label={`Asset tag row ${index + 1}`}
                        />
                      )}
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={row.condition}
                        onChange={(e) => updateRow(row.key, { condition: e.target.value as AssetCondition })}
                        className={cell}
                        aria-label={`Condition row ${index + 1}`}
                      >
                        {ASSET_CONDITIONS.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={row.unitCostCurrency}
                        onChange={(e) => updateRow(row.key, { unitCostCurrency: e.target.value as AssetCurrency })}
                        className={cell}
                        aria-label={`Currency row ${index + 1}`}
                      >
                        {ASSET_CURRENCIES.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={row.unitCost}
                        onChange={(e) => updateRow(row.key, { unitCost: e.target.value })}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        data-soft-keyboard-handled
                        className={cell}
                        placeholder="0.00"
                        aria-label={`Amount row ${index + 1}`}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <select
                        value={row.status}
                        onChange={(e) => updateRow(row.key, { status: e.target.value as AssetStatus })}
                        className={cell}
                        aria-label={`Status row ${index + 1}`}
                      >
                        {ASSET_STATUSES.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5">
                      <button
                        type="button"
                        onClick={() => removeRow(row.key)}
                        className="p-1 text-red-500 hover:text-red-700 disabled:text-gray-300"
                        disabled={rows.length <= 1}
                        title="Remove row"
                        aria-label={`Remove row ${index + 1}`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              onClick={addRow}
              className="mt-3 inline-flex items-center text-sm font-medium text-pink-600 hover:text-pink-800"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add row
            </button>
          </div>

          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4">
            <p className="text-sm text-gray-600">
              {namedCount === 1 ? '1 item ready to save' : `${namedCount} items ready to save`}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-lg bg-white px-5 py-2 font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || namedCount === 0}
                className="rounded-lg bg-pink-500 px-5 py-2 font-bold text-white hover:bg-pink-600 disabled:opacity-50"
              >
                {saving ? 'Saving…' : `Save ${namedCount || ''} item${namedCount === 1 ? '' : 's'}`.trim()}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetBulkAddModal;
